import { addDays, isSameDay } from "date-fns";

/**
 * Composable for shift deletion operations
 * @param {Ref<Array>} events - Reference to calendar events
 * @param {Object} shiftsService - Service for managing shifts
 * @returns {Object} - Methods for deleting shifts
 */
export function useShiftDelete(events, shiftsService) {
  /**
   * Delete a shift by ID
   * @param {String|Number} id - Shift ID
   * @returns {Object} - Result object
   */
  const deleteShiftById = async (id) => {
    try {
      await shiftsService.deleteShift(id);
      return { success: true, message: "Shift deleted successfully" };
    } catch (err) {
      return {
        success: false,
        message: `Failed to delete shift: ${err.message}`,
      };
    }
  };

  /**
   * Delete all shifts on a specific date
   * @param {Date} date - Date to delete shifts from
   * @returns {Object} - Result object
   */
  const deleteShiftsByDate = async (date) => {
    const toDelete = events.value.filter((e) =>
      isSameDay(new Date(e.start), new Date(date))
    );

    if (toDelete.length === 0) {
      return { success: false, message: "No shifts found for this date" };
    }

    try {
      let deletedCount = 0;
      for (const e of toDelete) {
        await shiftsService.deleteShift(e.id);
        deletedCount++;
      }

      return {
        success: true,
        message: `Deleted ${deletedCount} shifts successfully`,
      };
    } catch (err) {
      return {
        success: false,
        message: `Failed to delete shifts: ${err.message}`,
      };
    }
  };

  /**
   * Delete all shifts in a week
   * @param {Date} weekStart - Start date of the week
   * @returns {Object} - Result object
   */
  const deleteShiftsByWeek = async (weekStart) => {
    // Process all shifts in the target week
    
    // Use the same improved Saturday handling as in the copy function
    const toDelete = events.value.filter((e) => {
      const shiftStart = new Date(e.start);
      const weekEnd = addDays(weekStart, 6); // Saturday
      
      // Basic date-based check
      let isInWeek = shiftStart >= weekStart && shiftStart <= weekEnd;
      
      // Special handling for Saturday shifts
      if (shiftStart.getDay() === 6) {
        // For Saturday (day 6), use a more reliable method that handles month boundaries
        const saturday = addDays(weekStart, 6); // This is the actual Saturday
        const saturdayDate = saturday.getDate();
        const saturdayMonth = saturday.getMonth();
        
        // Check if this shift is on the correct Saturday (same date and month)
        const isCorrectSaturday = 
          shiftStart.getDate() === saturdayDate && 
          shiftStart.getMonth() === saturdayMonth;
        
        // If this is the right Saturday based on the date, force include it
        if (isCorrectSaturday) {
          isInWeek = true;
        }
      }
      
      return isInWeek;
    });

    if (toDelete.length === 0) {
      return { success: false, message: "No shifts found for this week" };
    }

    try {
      let deletedCount = 0;
      let saturdayCount = 0;
      
      // Count Saturday shifts to delete
      for (const e of toDelete) {
        if (new Date(e.start).getDay() === 6) {
          saturdayCount++;
        }
      }
      
      // Process deletion
      for (const e of toDelete) {
        await shiftsService.deleteShift(e.id);
        deletedCount++;
      }

      // Return success with count
      let message = `Deleted ${deletedCount} shifts successfully`;
      
      return {
        success: true,
        message: message,
      };
    } catch (err) {
      return {
        success: false,
        message: `Failed to delete shifts: ${err.message}`,
      };
    }
  };

  return {
    deleteShiftById,
    deleteShiftsByDate,
    deleteShiftsByWeek,
  };
}